// Physically-based atmospheric scattering (single-scattering Rayleigh + Mie), adapted for longfight from
// the Nishita/O'Neil model popularized by Maxime Heckel's "On rendering the sky, sunsets and planets".
//
// The raymarch (24 primary × 6 light steps) is EXPENSIVE per pixel, so we never run it in the per-frame
// path. Instead we BAKE it once into a cubemap (SkyBaker) at level-load; the sky then costs a single
// texture lookup per frame. Because the bake is keyed on a param set (sun direction + atmosphere
// coefficients), it stays dynamic-capable: re-bake only when the sun actually moves. The SAME GLSL also
// renders a planet's atmosphere from space (campaign globes) via a thin shell shader.

import * as THREE from "three";

export interface ScatterParams {
  sunDir: THREE.Vector3;   // normalized, world up = +Y
  sunI: number;            // sun radiance scale
  betaR: THREE.Vector3;    // Rayleigh scattering coefficients (wavelength tint)
  betaM: THREE.Vector3;    // Mie scattering coefficients (haze — colored, so dust can tint it)
  Hr: number;              // Rayleigh scale height
  Hm: number;              // Mie scale height
  planetR: number;         // planet radius
  atmoR: number;           // atmosphere top radius
  mieG: number;            // Mie phase asymmetry (0..~0.9, forward-scatter halo)
  exposure: number;        // tonemap exposure for the baked sky
}

/** Earth-like defaults (a "km" scale). Per-biome presets scale/tint these. */
export const EARTH_SCATTER: ScatterParams = {
  sunDir: new THREE.Vector3(0.6, 0.5, 0.6).normalize(),
  sunI: 7,
  betaR: new THREE.Vector3(5.8e-3, 13.5e-3, 33.1e-3),
  betaM: new THREE.Vector3(4e-3, 4e-3, 4e-3),
  Hr: 8, Hm: 1.2,
  planetR: 6371, atmoR: 6471,
  mieG: 0.76,
  exposure: 1.0,
};

// The scattering integral itself — shared by the sky bake and the planet-from-space shell.
// atmosphere(ro, rd) returns in-scattered LINEAR radiance toward the camera along the ray.
export const SCATTER_GLSL = /* glsl */ `
  #define PI 3.141592653589793
  uniform vec3 uSunDir; uniform float uSunI;
  uniform vec3 uBetaR; uniform vec3 uBetaM;
  uniform float uHr; uniform float uHm;
  uniform float uPlanetR; uniform float uAtmoR; uniform float uMieG;

  // far intersection distance of ray (ro,rd) with a sphere radius R at origin (ro assumed inside it)
  float atmoExit(vec3 ro, vec3 rd, float R){
    float b = dot(ro, rd);
    float c = dot(ro, ro) - R * R;
    float d = b * b - c;
    if (d < 0.0) return -1.0;
    return -b + sqrt(d);
  }
  // nearest intersection of ray with the planet (returns signed t; <=0 means not blocked ahead)
  float planetHit(vec3 ro, vec3 rd, float R){
    float b = dot(ro, rd);
    float c = dot(ro, ro) - R * R;
    float d = b * b - c;
    if (d < 0.0) return -1.0;
    return -b - sqrt(d);
  }

  vec3 atmosphere(vec3 ro, vec3 rd){
    float tMax = atmoExit(ro, rd, uAtmoR);
    if (tMax < 0.0) return vec3(0.0);            // ray never enters the atmosphere
    float pt = planetHit(ro, rd, uPlanetR);
    if (pt > 0.0) tMax = min(tMax, pt);          // clamp the march to the ground

    const int STEPS = 24;
    const int LSTEPS = 6;
    float seg = tMax / float(STEPS);
    float t = 0.0;
    vec3 sumR = vec3(0.0); vec3 sumM = vec3(0.0);
    float odR = 0.0; float odM = 0.0;            // accumulated optical depth along the view ray

    for (int i = 0; i < STEPS; i++){
      vec3 p = ro + rd * (t + seg * 0.5);
      float h = length(p) - uPlanetR;
      float dR = exp(-h / uHr) * seg;
      float dM = exp(-h / uHm) * seg;
      odR += dR; odM += dM;

      // optical depth from this sample toward the sun (light march)
      float lt = atmoExit(p, uSunDir, uAtmoR);
      float lseg = lt / float(LSTEPS);
      float lodR = 0.0; float lodM = 0.0; float l = 0.0; bool ground = false;
      for (int j = 0; j < LSTEPS; j++){
        vec3 lp = p + uSunDir * (l + lseg * 0.5);
        float lh = length(lp) - uPlanetR;
        if (lh < 0.0){ ground = true; break; }   // the sun is occluded by the planet here
        lodR += exp(-lh / uHr) * lseg;
        lodM += exp(-lh / uHm) * lseg;
        l += lseg;
      }
      if (!ground){
        vec3 tau = uBetaR * (odR + lodR) + uBetaM * 1.1 * (odM + lodM);
        vec3 att = exp(-tau);                    // Beer-Lambert transmittance to sun and back
        sumR += dR * att; sumM += dM * att;
      }
      t += seg;
    }

    float mu = dot(rd, uSunDir);
    float pR = 3.0 / (16.0 * PI) * (1.0 + mu * mu);
    float g = uMieG;
    float pM = 3.0 / (8.0 * PI) * ((1.0 - g * g) * (1.0 + mu * mu)) /
               ((2.0 + g * g) * pow(max(0.0, 1.0 + g * g - 2.0 * g * mu), 1.5));
    return uSunI * (sumR * uBetaR * pR + sumM * uBetaM * pM);
  }
`;

// Narkowicz ACES approximation + exposure + sRGB — matches the scene's ACESFilmic tonemapping so the
// baked sky sits at the same exposure as everything else (raw ShaderMaterials aren't auto-tonemapped).
export const TONEMAP_GLSL = /* glsl */ `
  uniform float uExposure;
  vec3 acesTonemap(vec3 x){
    return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
  }
  vec3 toDisplay(vec3 lin){
    return pow(acesTonemap(lin * uExposure), vec3(1.0 / 2.2));
  }
`;

function uniformsFor(p: ScatterParams): Record<string, THREE.IUniform> {
  return {
    uSunDir: { value: p.sunDir.clone() },
    uSunI: { value: p.sunI },
    uBetaR: { value: p.betaR.clone() },
    uBetaM: { value: p.betaM.clone() },
    uHr: { value: p.Hr },
    uHm: { value: p.Hm },
    uPlanetR: { value: p.planetR },
    uAtmoR: { value: p.atmoR },
    uMieG: { value: p.mieG },
    uExposure: { value: p.exposure },
  };
}

const BAKE_VERT = /* glsl */ `
  varying vec3 vDir;
  void main(){ vDir = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
// observer sits just above the surface; each cube-face pixel bakes the sky radiance in its direction,
// plus a crisp sun disc so the star itself reads on thin-air worlds.
const BAKE_FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vDir;
  uniform float uObserverH; uniform vec3 uSunTint; uniform float uSunDisc;
  ${SCATTER_GLSL}
  ${TONEMAP_GLSL}
  void main(){
    vec3 rd = normalize(vDir);
    vec3 ro = vec3(0.0, uPlanetR + uObserverH, 0.0);
    vec3 sky = atmosphere(ro, rd);
    float mu = dot(rd, uSunDir);
    float disc = smoothstep(uSunDisc, uSunDisc * 0.35 + 0.65, mu); // tight bright sun
    sky += uSunTint * disc * uSunI * 0.02;
    gl_FragColor = vec4(toDisplay(sky), 1.0);
  }
`;

/** Bakes the ground-view sky into a cubemap. Cheap to sample (one texture lookup), re-bake on sun move. */
export class SkyBaker {
  readonly rt: THREE.WebGLCubeRenderTarget;
  private scene = new THREE.Scene();
  private cam: THREE.CubeCamera;
  private mat: THREE.ShaderMaterial;

  constructor(size = 128) {
    this.rt = new THREE.WebGLCubeRenderTarget(size, { type: THREE.HalfFloatType });
    this.cam = new THREE.CubeCamera(0.1, 100, this.rt);
    this.mat = new THREE.ShaderMaterial({
      uniforms: { ...uniformsFor(EARTH_SCATTER), uObserverH: { value: 1.0 }, uSunTint: { value: new THREE.Color(0xffffff) }, uSunDisc: { value: 0.9995 } },
      vertexShader: BAKE_VERT, fragmentShader: BAKE_FRAG, side: THREE.BackSide, depthWrite: false, depthTest: false,
    });
    const dome = new THREE.Mesh(new THREE.SphereGeometry(10, 24, 16), this.mat);
    this.scene.add(dome);
  }

  /** (Re)compute the cubemap for a param set + sun tint. Call at install and whenever the sun moves. */
  bake(renderer: THREE.WebGLRenderer, p: ScatterParams, sunTint: THREE.Color, observerH: number, sunDisc: number): void {
    const u = this.mat.uniforms;
    u.uSunDir.value.copy(p.sunDir);
    u.uSunI.value = p.sunI;
    u.uBetaR.value.copy(p.betaR);
    u.uBetaM.value.copy(p.betaM);
    u.uHr.value = p.Hr; u.uHm.value = p.Hm;
    u.uPlanetR.value = p.planetR; u.uAtmoR.value = p.atmoR; u.uMieG.value = p.mieG;
    u.uExposure.value = p.exposure;
    u.uObserverH.value = observerH;
    u.uSunTint.value.copy(sunTint);
    u.uSunDisc.value = sunDisc;
    this.cam.update(renderer, this.scene);
  }

  get texture(): THREE.CubeTexture { return this.rt.texture; }

  dispose(): void {
    this.rt.dispose();
    this.mat.dispose();
    this.scene.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
  }
}

export { uniformsFor as scatterUniforms };

// ============================================================================================
// PLANET ATMOSPHERE FROM SPACE — a thin additive shell around a globe (campaign / planet lab). Same
// Rayleigh+Mie model, but the camera is OUTSIDE the atmosphere, so the ray enters the atmosphere
// sphere (near root) and marches to the exit or the planet surface. planetR is fixed at 1.0 (the unit
// planet mesh); every other coefficient is a live-tunable uniform so it can be dialed in with sliders.
// ============================================================================================

export interface AtmoConfig {
  atmoR: number;                          // atmosphere top radius (planet surface = 1.0)
  sunI: number;                           // sun radiance scale
  betaR: [number, number, number];        // Rayleigh coefficients (wavelength tint)
  betaM: [number, number, number];        // Mie coefficients (haze tint)
  Hr: number; Hm: number;                 // scale heights (fraction of the unit radius)
  mieG: number;                           // Mie phase asymmetry
  exposure: number;                       // additive tonemap exposure
  strength: number;                       // overall glow multiplier
  steps: number; lSteps: number;          // raymarch quality (view / light)
  sunElevDeg: number; sunAziDeg: number;  // sun direction
  edge: number;                           // 0..1 fraction of the atmosphere that fades to 0 at the top
}

export const DEFAULT_ATMO: AtmoConfig = {
  atmoR: 1.15, sunI: 18,
  betaR: [3.0, 7.0, 14.0], betaM: [5.0, 5.0, 5.0],
  Hr: 0.10, Hm: 0.03, mieG: 0.76,
  exposure: 1.5, strength: 1.0,
  steps: 14, lSteps: 5,
  sunElevDeg: 25, sunAziDeg: 40,
  edge: 0.55,
};

function atmoSunDir(elevDeg: number, aziDeg: number): THREE.Vector3 {
  const e = (elevDeg * Math.PI) / 180, a = (aziDeg * Math.PI) / 180;
  return new THREE.Vector3(Math.cos(e) * Math.sin(a), Math.sin(e), Math.cos(e) * Math.cos(a)).normalize();
}

const ATMO_SHELL_GLSL = /* glsl */ `
  #define PI 3.141592653589793
  uniform vec3 uASunDir; uniform float uASunI;
  uniform vec3 uABetaR; uniform vec3 uABetaM;
  uniform float uAHr; uniform float uAHm; uniform float uAMieG;
  uniform float uAAtmoR; uniform float uAStrength; uniform float uAEdge;
  uniform int uASteps; uniform int uALSteps;
  const float PLANET_R = 1.0;

  bool raySphere2(vec3 ro, vec3 rd, float R, out float t0, out float t1){
    float b = dot(ro, rd); float c = dot(ro, ro) - R * R; float d = b * b - c;
    if (d < 0.0) return false; d = sqrt(d); t0 = -b - d; t1 = -b + d; return true;
  }
  // altitude window: density smoothly reaches 0 at the atmosphere TOP so the outer edge always fades into
  // space instead of cutting off as a hard shell — independent of the scale-height sliders.
  float altFade(float rr){
    float band = max(1e-4, (uAAtmoR - PLANET_R) * uAEdge);
    return 1.0 - smoothstep(uAAtmoR - band, uAAtmoR, rr);
  }
  // returns vec4(in-scattered glow, view-ray TRANSMITTANCE of what's behind). T=1 = nothing attenuated.
  vec4 atmosphereSpace(vec3 ro, vec3 rd){
    float a0, a1;
    if (!raySphere2(ro, rd, uAAtmoR, a0, a1)) return vec4(0.0, 0.0, 0.0, 1.0);
    a0 = max(a0, 0.0);
    float end = a1;
    float p0, p1;
    if (raySphere2(ro, rd, PLANET_R, p0, p1)) { if (p1 > 0.0) end = min(end, max(p0, 0.0)); } // stop at surface
    if (end <= a0) return vec4(0.0, 0.0, 0.0, 1.0);
    float seg = (end - a0) / float(uASteps);
    float t = a0 + seg * 0.5;
    vec3 sumR = vec3(0.0); vec3 sumM = vec3(0.0);
    float odR = 0.0; float odM = 0.0;
    for (int i = 0; i < 64; i++){ if (i >= uASteps) break;
      vec3 pos = ro + rd * t;
      float rr = length(pos);
      float h = rr - PLANET_R;
      float fv = altFade(rr);
      float dR = exp(-h / uAHr) * seg * fv; float dM = exp(-h / uAHm) * seg * fv;
      odR += dR; odM += dM;
      float l0, l1; raySphere2(pos, uASunDir, uAAtmoR, l0, l1);
      float lseg = l1 / float(uALSteps); float lt = lseg * 0.5;
      float lodR = 0.0; float lodM = 0.0; bool occ = false;
      for (int j = 0; j < 32; j++){ if (j >= uALSteps) break;
        vec3 lp = pos + uASunDir * lt;
        float lrr = length(lp);
        float lh = lrr - PLANET_R;
        if (lh < 0.0){ occ = true; break; }
        float lf = altFade(lrr);
        lodR += exp(-lh / uAHr) * lseg * lf; lodM += exp(-lh / uAHm) * lseg * lf; lt += lseg;
      }
      if (!occ){
        vec3 tau = uABetaR * (odR + lodR) + uABetaM * 1.1 * (odM + lodM);
        vec3 att = exp(-tau);
        sumR += dR * att; sumM += dM * att;
      }
      t += seg;
    }
    float mu = dot(rd, uASunDir);
    float pR = 3.0 / (16.0 * PI) * (1.0 + mu * mu);
    float g = uAMieG;
    float pM = 3.0 / (8.0 * PI) * ((1.0 - g * g) * (1.0 + mu * mu)) /
               ((2.0 + g * g) * pow(max(0.0, 1.0 + g * g - 2.0 * g * mu), 1.5));
    vec3 inScatter = uASunI * (sumR * uABetaR * pR + sumM * uABetaM * pM) * uAStrength;
    // TRANSMITTANCE of whatever is behind (surface / space) along the view ray — Beer-Lambert over the
    // accumulated view-ray optical depth. This is the piece the additive-only shell was MISSING: near the
    // limb the path is long → T→0 → the surface dims + dissolves into the haze (aerial perspective).
    vec3 tau = uABetaR * odR + uABetaM * 1.1 * odM;
    float T = exp(-dot(tau, vec3(0.30, 0.59, 0.11)));
    return vec4(inScatter, T);
  }
`;
const ATMO_SHELL_VERT = /* glsl */ `
  varying vec3 vWorld; varying vec3 vRo;
  uniform float uAPlanetWorldR;
  void main(){
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorld = wp.xyz;
    // camera relative to the planet center, NORMALIZED to planet-radii so the raymarch (PLANET_R=1) stays
    // correct at ANY world size — this is what lets planets vary in size without breaking the atmosphere.
    vRo = (cameraPosition - (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz) / uAPlanetWorldR;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;
const ATMO_SHELL_FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vWorld; varying vec3 vRo;
  uniform float uAExposure; uniform float uAFade; uniform float uAGlow;
  ${ATMO_SHELL_GLSL}
  void main(){
    vec3 rd = normalize(vWorld - cameraPosition);
    vec4 atmo = atmosphereSpace(vRo, rd);
    vec3 col = 1.0 - exp(-atmo.rgb * uAExposure); // soft tonemap so the glow never blows out
    // custom blend (ONE, SRC_ALPHA): out = inScatter + surface*srcAlpha. srcAlpha ATTENUATES the surface
    // behind the shell — fade 1 (atmosphere off → surface untouched) → T (full aerial perspective) on zoom.
    gl_FragColor = vec4(col * uAFade * uAGlow, mix(1.0, atmo.a, uAFade));
  }
`;

/** An additive atmosphere-shell material shared across planets; `apply(cfg)` pushes live slider values. */
export function makePlanetAtmosphere(): { material: THREE.ShaderMaterial; apply: (c: AtmoConfig) => void } {
  const uniforms: Record<string, THREE.IUniform> = {
    uASunDir: { value: new THREE.Vector3(0, 0, 1) }, uASunI: { value: DEFAULT_ATMO.sunI },
    uABetaR: { value: new THREE.Vector3(...DEFAULT_ATMO.betaR) }, uABetaM: { value: new THREE.Vector3(...DEFAULT_ATMO.betaM) },
    uAHr: { value: DEFAULT_ATMO.Hr }, uAHm: { value: DEFAULT_ATMO.Hm }, uAMieG: { value: DEFAULT_ATMO.mieG },
    uAAtmoR: { value: DEFAULT_ATMO.atmoR }, uAStrength: { value: DEFAULT_ATMO.strength },
    uAExposure: { value: DEFAULT_ATMO.exposure }, uAEdge: { value: DEFAULT_ATMO.edge },
    uASteps: { value: DEFAULT_ATMO.steps }, uALSteps: { value: DEFAULT_ATMO.lSteps },
    uAPlanetWorldR: { value: 1 }, // set to the planet's world radius when the globe is scaled by size
    uAFade: { value: 1 }, // 1 in the lab; the campaign ramps it 0→1 as you zoom into the planet
    uAGlow: { value: 1 }, // in-scatter brightness scale (campaign lowers it for bloom headroom; lab = 1)
  };
  const material = new THREE.ShaderMaterial({
    uniforms, vertexShader: ATMO_SHELL_VERT, fragmentShader: ATMO_SHELL_FRAG,
    transparent: true, depthWrite: false, side: THREE.FrontSide,
    // two-term blend so ONE shell pass can BOTH add in-scatter AND attenuate the surface behind it:
    // out.rgb = shell.rgb·ONE + surface.rgb·shell.a. (Plain additive could never dim the surface.)
    blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor, blendDst: THREE.SrcAlphaFactor,
  });
  const apply = (c: AtmoConfig): void => {
    uniforms.uASunDir.value.copy(atmoSunDir(c.sunElevDeg, c.sunAziDeg));
    uniforms.uASunI.value = c.sunI;
    uniforms.uABetaR.value.set(...c.betaR); uniforms.uABetaM.value.set(...c.betaM);
    uniforms.uAHr.value = c.Hr; uniforms.uAHm.value = c.Hm; uniforms.uAMieG.value = c.mieG;
    uniforms.uAAtmoR.value = c.atmoR; uniforms.uAStrength.value = c.strength;
    uniforms.uAExposure.value = c.exposure; uniforms.uAEdge.value = c.edge;
    uniforms.uASteps.value = Math.round(c.steps); uniforms.uALSteps.value = Math.round(c.lSteps);
  };
  apply(DEFAULT_ATMO);
  return { material, apply };
}
